import { useState } from 'react'
import { useAuditFilters, useAuditLog } from '../api/hooks'
import { useAuth } from '../auth/AuthContext'
import type { AuditEntry } from '../api/types'
import { formatDateTimeSeconds } from '../components/formatTime'

/**
 * The audit log (§11, §12 Phase 8).
 *
 * Read-only, and there is no way to add or edit a row from here — the entries are
 * written by the endpoints that make the changes, and a screen that could write one
 * would be a screen that could forge one.
 *
 * Admin only, matching the API: the before/after payloads include full driver SQL text,
 * which §11 keeps away from a Viewer even on its own screen.
 */

const PAGE_SIZE = 100

/** Colour carries meaning: a deletion is the one worth spotting from across the room. */
const ACTION_STYLE: Record<string, { bg: string; fg: string }> = {
  Insert: { bg: 'var(--color-ok-wash)', fg: 'var(--color-ok)' },
  Update: { bg: 'var(--color-accent-wash)', fg: 'var(--color-accent)' },
  Delete: { bg: 'var(--color-error-wash)', fg: 'var(--color-error)' },
  Approve: { bg: 'var(--color-ok-wash)', fg: 'var(--color-ok)' },
  Reject: { bg: 'var(--color-warn-wash)', fg: 'var(--color-warn)' },
  Execute: { bg: 'var(--color-panel-alt)', fg: 'var(--color-muted)' },
}

export function AuditLog() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('Admin')

  const [tableName, setTableName] = useState('')
  const [action, setAction] = useState('')
  const [userName, setUserName] = useState('')
  const [keyValue, setKeyValue] = useState('')
  const [skip, setSkip] = useState(0)
  const [expanded, setExpanded] = useState<number | null>(null)

  const { data: filters } = useAuditFilters(isAdmin)
  const { data: page, isLoading } = useAuditLog(isAdmin, {
    tableName,
    action,
    userName,
    keyValue,
    skip,
    take: PAGE_SIZE,
  })

  if (!isAdmin) {
    return (
      <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
        The audit log is available to administrators. It records every change with the
        full before and after values, including driver SQL.
      </p>
    )
  }

  const entries = page?.entries ?? []
  const total = page?.totalCount ?? 0

  /** Any filter change starts at the beginning; page 4 of the old filter means nothing. */
  function change(setter: (value: string) => void) {
    return (value: string) => {
      setter(value)
      setSkip(0)
      setExpanded(null)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div
        className="flex flex-wrap items-end gap-3 border-b px-4 py-2"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
      >
        <h1 className="mb-1 text-[14px] font-semibold">Audit log</h1>

        <label className="flex flex-col gap-1 text-[12px]">
          <span className="col-head">table</span>
          <select
            className="input w-52"
            value={tableName}
            onChange={(e) => change(setTableName)(e.target.value)}
          >
            <option value="">All</option>
            {filters?.tableNames.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[12px]">
          <span className="col-head">action</span>
          <select
            className="input w-32"
            value={action}
            onChange={(e) => change(setAction)(e.target.value)}
          >
            <option value="">All</option>
            {filters?.actions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[12px]">
          <span className="col-head">user</span>
          <select
            className="input w-44"
            value={userName}
            onChange={(e) => change(setUserName)(e.target.value)}
          >
            <option value="">All</option>
            {filters?.users.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[12px]">
          <span className="col-head">key</span>
          <input
            className="input input-mono w-28"
            value={keyValue}
            onChange={(e) => change(setKeyValue)(e.target.value)}
            placeholder="id"
            title="The primary key of the changed row. With a table selected, this is the fastest way to see one entity's whole history."
          />
        </label>

        <span className="ml-auto text-[12px]" style={{ color: 'var(--color-muted)' }}>
          {total === 0
            ? 'No entries'
            : `${skip + 1}–${Math.min(skip + entries.length, total)} of ${total.toLocaleString()}`}
        </span>

        <button
          className="btn btn-sm"
          onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
          disabled={skip === 0}
        >
          Previous
        </button>
        <button
          className="btn btn-sm"
          onClick={() => setSkip(skip + PAGE_SIZE)}
          disabled={skip + entries.length >= total}
        >
          Next
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto" style={{ background: 'var(--color-panel)' }}>
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10" style={{ background: 'var(--color-panel-alt)' }}>
            <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
              <th className="col-head px-3 py-1.5">When</th>
              <th className="col-head px-3 py-1.5">User</th>
              <th className="col-head px-3 py-1.5">Action</th>
              <th className="col-head px-3 py-1.5">Table</th>
              <th className="col-head px-3 py-1.5">Key</th>
              <th className="col-head px-3 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-[12px]" style={{ color: 'var(--color-faint)' }}>
                  Loading…
                </td>
              </tr>
            )}

            {!isLoading && entries.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
                  Nothing matches those filters.
                </td>
              </tr>
            )}

            {entries.map((entry) => (
              <AuditRow
                key={entry.auditLogId}
                entry={entry}
                expanded={expanded === entry.auditLogId}
                onToggle={() =>
                  setExpanded(expanded === entry.auditLogId ? null : entry.auditLogId)
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function AuditRow({
  entry,
  expanded,
  onToggle,
}: {
  entry: AuditEntry
  expanded: boolean
  onToggle: () => void
}) {
  const style = ACTION_STYLE[entry.action] ?? {
    bg: 'var(--color-panel-alt)',
    fg: 'var(--color-muted)',
  }

  const hasPayload = entry.beforeJson !== null || entry.afterJson !== null

  return (
    <>
      <tr
        className="grid-row border-b"
        style={{ borderColor: 'var(--color-rule-soft)' }}
        tabIndex={0}
        onClick={hasPayload ? onToggle : undefined}
      >
        <td className="ident px-3 py-1" style={{ color: 'var(--color-muted)' }}>
          {formatDateTimeSeconds(entry.createdUtc)}
        </td>
        <td className="px-3 py-1 text-[12px]">{entry.userName}</td>
        <td className="px-3 py-1">
          <span className="badge" style={{ background: style.bg, color: style.fg }}>
            {entry.action}
          </span>
        </td>
        <td className="ident px-3 py-1">{entry.tableName}</td>
        <td className="ident px-3 py-1" style={{ color: 'var(--color-muted)' }}>
          {entry.keyValue}
        </td>
        <td className="px-3 py-1 text-right">
          {hasPayload && (
            <span className="text-[11px]" style={{ color: 'var(--color-accent)' }}>
              {expanded ? 'hide' : 'detail'}
            </span>
          )}
        </td>
      </tr>

      {expanded && (
        <tr style={{ background: 'var(--color-panel-alt)' }}>
          <td colSpan={6} className="px-3 py-2">
            {/* Before and after side by side. The pair is the point of the row — either
                one alone leaves the reader guessing at what changed. */}
            <div className="grid gap-3 md:grid-cols-2">
              <Payload label="before" json={entry.beforeJson} />
              <Payload label="after" json={entry.afterJson} />
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function Payload({ label, json }: { label: string; json: string | null }) {
  return (
    <div>
      <div className="col-head mb-1">{label}</div>
      <pre
        className="ident overflow-auto rounded p-2 text-[11px]"
        style={{
          background: 'var(--color-panel)',
          color: json === null ? 'var(--color-faint)' : 'var(--color-ink)',
          maxHeight: '18rem',
        }}
      >
        {json === null ? '—' : format(json)}
      </pre>
    </div>
  )
}

/** Pretty-prints when it can, and shows the raw text when it cannot rather than nothing. */
function format(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2)
  } catch {
    return json
  }
}
